import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, ArrowUpRight, Loader2 } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { api } from "../../lib/api";
import { useSettings, useServices } from "../../lib/queries";

export default function Contact() {
  const { t, pick } = useLang();
  const { data: settings } = useSettings();
  const { data: services = [] } = useServices();
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "", service: "", budget: "", message: "",
  });
  const [busy, setBusy] = useState(false);

  const bind = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error(t("form_error"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/messages", form);
      toast.success(t("form_sent"));
      setForm({ name: "", company: "", phone: "", email: "", service: "", budget: "", message: "" });
    } catch (err) {
      toast.error(t("form_error"));
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/15 py-4 text-white placeholder-white/30 focus:outline-none focus:border-[#A855F7] transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="py-32 relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#A855F7]" />
              <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
                {t("contact_kicker")}
              </span>
            </div>
            <h2 className="font-display text-6xl md:text-7xl uppercase tracking-tighter text-white leading-[0.9]">
              {t("contact_title")}
            </h2>
            <p className="mt-8 text-white/60 text-lg max-w-md">{t("contact_desc")}</p>

            {settings && (
              <ul className="mt-12 space-y-4 text-white/80">
                <li className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center"><Phone size={14} /></span>
                  <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="hover:text-[#A855F7] transition-colors">{settings.phone}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center"><Mail size={14} /></span>
                  <a href={`mailto:${settings.email}`} className="hover:text-[#A855F7] transition-colors">{settings.email}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center"><Instagram size={14} /></span>
                  <a href={settings.instagram} target="_blank" rel="noreferrer" className="hover:text-[#A855F7] transition-colors">@ixirli_</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center"><MapPin size={14} /></span>
                  {settings.location}
                </li>
              </ul>
            )}
          </div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2"
          >
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_name")}</span>
              <input data-testid="input-name" className={inputClass} type="text" required {...bind("name")} />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_company")}</span>
              <input data-testid="input-company" className={inputClass} type="text" {...bind("company")} />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_phone")}</span>
              <input data-testid="input-phone" className={inputClass} type="tel" required {...bind("phone")} />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_email")}</span>
              <input data-testid="input-email" className={inputClass} type="email" required {...bind("email")} />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_service")}</span>
              <select data-testid="input-service" className={inputClass + " appearance-none"} {...bind("service")}>
                <option value="" className="bg-[#101018]">—</option>
                {services.map((s) => (
                  <option key={s.slug} value={s.slug} className="bg-[#101018]">{pick(s, "title")}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_budget")}</span>
              <input data-testid="input-budget" className={inputClass} type="text" placeholder="₺ / $" {...bind("budget")} />
            </label>
            <label className="block md:col-span-2">
              <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono-ui">{t("form_message")}</span>
              <textarea data-testid="input-message" required rows={4} className={inputClass + " resize-none"} {...bind("message")} />
            </label>
            <div className="md:col-span-2 pt-6">
              <button data-testid="form-submit" disabled={busy} className="btn-primary disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                {t("form_submit")}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
